import { createContext, useContext, useState, useEffect } from "react";

const UserContext = createContext(undefined);

export function UserProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [token, setTokenState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user data from localStorage on mount
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("user");
      const savedToken = localStorage.getItem("token");

      console.log("💾 Loading user from localStorage:", {
        hasUser: !!savedUser,
        hasToken: !!savedToken,
      });

      if (savedUser && savedToken) {
        const parsedUser = JSON.parse(savedUser);
        setUserState(parsedUser);
        setTokenState(savedToken);
        console.log("🔐 User loaded successfully:", parsedUser.email);
      }
    } catch (error) {
      console.error("❌ Error loading user from localStorage:", error);
      // Clear corrupted data
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setUser = (newUser) => {
    console.log("🔐 Setting user:", newUser?.email || "null");
    setUserState(newUser);

    if (newUser) {
      localStorage.setItem("user", JSON.stringify(newUser));
      console.log("💾 User saved to localStorage");
    } else {
      localStorage.removeItem("user");
      console.log("💾 User removed from localStorage");
    }
  };

  const setToken = (newToken) => {
    console.log("🔐 Setting token:", newToken ? "present" : "null");
    setTokenState(newToken);

    if (newToken) {
      localStorage.setItem("token", newToken);
      console.log("💾 Token saved to localStorage");
    } else {
      localStorage.removeItem("token");
      console.log("💾 Token removed from localStorage");
    }
  };

  const setLoading = (loading) => {
    setIsLoading(loading);
  };

  const logout = () => {
    console.log("🔐 Logging out user");
    setUser(null);
    setToken(null);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        token,
        isLoading,
        setUser,
        setToken,
        setLoading,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export function useAuth() {
  const { user, token, logout } = useUser();
  return {
    user,
    token,
    isAuthenticated: !!user && !!token,
    logout,
  };
}

// Helper function to load token from storage (for API hooks)
export function loadTokenFromStorage() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("token");
}

